<template>
    <div class="lesson-answer-sheet">
        <h3 class="title">{{ $t('common.attachments') }}</h3>
        <ul class="attachments-list">
            <li
                class="attachment"
                v-for="file in lessonIn.files"
                :key="file.id">
                <div class="attachment-content">
                    <div
                        class="icon icon-type"
                        :class="getFileType(file.extension)"></div>
                    <div class="file-name">
                        {{ file.name }}
                        <span class="file-size text-muted">
                            {{ file.size }}
                        </span>
                    </div>
                </div>

                <div class="attachment-actions">
                    <b-button
                        class="btn-download btn-file-download"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :href="file.path"
                        :title="$t('common.download_file')"
                        :download="file.path"
                        target="_blank">
                    </b-button>

                    <b-button
                        class="btn-remove"
                        v-if="editable"
                        v-b-tooltip.hover
                        :title="$t('files.delete_file')"
                        variant="danger-icon-only"
                        @click="removeFile(file)">
                    </b-button>
                </div>
            </li>
        </ul>

        <div
            class="not-found with-header empty-attachment-list"
            v-if="lessonIn.files.length === 0">
            <div
                class="btn-add-circle icon"
                v-if="editable"
                v-b-tooltip.hover
                :title="$t('common.add_files')"
                @click="openFileUpload"></div>
            <div class="alert-notice">{{ $t('files.no_file_uploaded') }}</div>
        </div>

        <div
            class="new-attachment"
            v-if="editable && lessonIn.files.length > 0">
            <b-button
                class="btn-add"
                variant="primary"
                @click="openFileUpload">
                {{ $t('common.add_files') }}
            </b-button>
        </div>

        <design-2021-lesson-manual-lesson-upload-file-modal-component
            ref="upload-file-modal"
            :lesson-id="lessonIn.id"
            @lesson-updated="lessonUpdated">
        </design-2021-lesson-manual-lesson-upload-file-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-lesson-manual-lesson-files-component',
    props: {
        lessonIn: {
            type: Object,
            required: true,
        },
        editable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            addingFilesIsInProgress: false,
        };
    },
    methods: {
        openFileUpload() {
            this.$refs['upload-file-modal'].show();
        },

        lessonUpdated(newLesson) {
            this.$emit('lesson-updated', newLesson);
        },
        removeFile(file) {
            this.alert
                .confirm(
                    this.$t('common.remove') + ' ' + file.name,
                    this.$t('common.are_you_sure_you_want_to_delete_this_item'),
                )
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        this.axios.delete(this.routes.deleteLessonFile(this.lessonIn.id, file.id)).then(
                            (response) => {
                                this.$emit('lesson-updated', response.data);
                            },
                            (error) => {
                                return this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },

        getFileType(filetype) {
            switch (filetype) {
                case 'jpeg':
                case 'jpg':
                case 'png':
                case 'jif':
                case 'jfif':
                case 'gif':
                    return 'btn-image-type ';

                case 'pdf':
                    return 'btn-file-type ';

                case 'doc':
                case 'docx':
                    return 'btn-doc-type ';

                case 'ppt':
                case 'pptx':
                    return 'btn-doc-type ';

                case 'xls':
                case 'xlx':
                    return 'btn-doc-type ';

                case 'cda':
                case 'mp3':
                case 'oga':
                    return 'btn-audio-type ';

                case 'mov':
                case 'mpeg':
                case 'mkv':
                case 'ogv':
                case 'webm':
                case '3gp':
                    return 'btn-video-type ';

                default:
                    return 'btn-file-type ';
            }
        },
    },
};
</script>

<style scoped lang="scss">
.attachments-list {
    list-style: none;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: map-get($grid-gutter-spacing, 'fifteen') $grid-gutter-width;

    .attachment {
        flex: 1 0 auto;

        &,
        .attachment-content {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            flex-wrap: wrap;
        }
    }

    .attachment-content ::v-deep {
        column-gap: map-get($grid-gutter-spacing, 'ten');

        .icon {
            align-self: center;
            padding: 10px;
            background: var(--intermediateColor, $secondary-background-color);
            border-radius: $border-radius-content;
            color: var(--intermediateLighterColor, $primary);
            font-size: 1.3125rem;
        }

        .file-name {
            display: flex;
            flex-direction: column;
            font-size: $base-font-size;
            font-family: $headings-font-family;
            font-weight: $font-weight-bold;
            align-self: center;
            word-break: break-word;

            .file-size {
                font-size: 0.75rem;
                font-weight: $font-weight-normal;
                line-height: 1;
            }
        }

        .btn-file-download {
            &:before {
                margin-right: 0;
            }
        }
    }

    .attachment-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        column-gap: map-get($grid-gutter-spacing, 'eight');
        margin-left: auto;
    }
}

.new-attachment {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
}

@media (min-width: map-get($grid-breakpoints, 'xxl')) {
    .attachments-list {
        .attachment {
            flex: 1 0 calc(50% - 15px);
            max-width: calc(50% - 15px);
        }
    }
}
</style>
